import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileText, Play } from "lucide-react";

import {
  C,
  FONT,
  footerBtnStyle,
  outlineBtnStyle,
  tabBtnStyle,
  thStyle,
} from "./styles";
import { fmtDate, fmtMoney } from "./helpers";
import { usePayRuns } from "./usePayRuns";
import FilterMenu from "./components/FilterMenu";
import PayRunRow from "./components/PayRunRow";
import type { Counts, PeriodType, SortType, TabType } from "./types";

export default function PayRuns() {
  const navigate = useNavigate();
  const { data: runs = [], isLoading: loading, error, refetch } = usePayRuns();
  const [tab, setTab] = useState<TabType>("all");
  const [sort, setSort] = useState<SortType>("newest");
  const [period, setPeriod] = useState<PeriodType>("all");
  const [company] = useState<string>(
    localStorage.getItem("company_name") || ""
  );

  const counts = useMemo<Counts>(
    () => ({
      all: runs.length,
      drafts: runs.filter((r) => r.status === "draft").length,
      finalized: runs.filter((r) => r.status === "finalized").length,
    }),
    [runs]
  );

  const filtered = useMemo(() => {
    let list = runs.slice();
    if (tab === "drafts") list = list.filter((r) => r.status === "draft");
    else if (tab === "finalized")
      list = list.filter((r) => r.status === "finalized");

    if (period !== "all") {
      const now = new Date();
      let cutoff: Date | null = null;
      if (period === "this-year") cutoff = new Date(now.getFullYear(), 0, 1);
      else if (period === "last-quarter") {
        cutoff = new Date(now);
        cutoff.setMonth(cutoff.getMonth() - 3);
      } else if (period === "this-month")
        cutoff = new Date(now.getFullYear(), now.getMonth(), 1);
      if (cutoff)
        list = list.filter(
          (r) => r.pay_date && new Date(r.pay_date) >= cutoff!
        );
    }

    if (sort === "newest")
      list.sort(
        (a, b) =>
          new Date(b.pay_date || 0).getTime() -
          new Date(a.pay_date || 0).getTime()
      );
    else if (sort === "oldest")
      list.sort(
        (a, b) =>
          new Date(a.pay_date || 0).getTime() -
          new Date(b.pay_date || 0).getTime()
      );
    else if (sort === "net-high")
      list.sort((a, b) => Number(b.total_net || 0) - Number(a.total_net || 0));
    else if (sort === "net-low")
      list.sort((a, b) => Number(a.total_net || 0) - Number(b.total_net || 0));

    return list;
  }, [runs, tab, sort, period]);

  const sectionTitle =
    tab === "drafts"
      ? "Drafts to continue"
      : tab === "finalized"
      ? "Finalized pay runs"
      : "Pay run history";

  const errMsg = error instanceof Error ? error.message : null;

  const handleExportCSV = () => {
    const header = ["Pay period", "Pay date", "Employees", "Net total", "Status"];
    const rows = filtered.map((r) => [
      fmtDate(r.pay_period_start) + " to " + fmtDate(r.pay_period_end),
      fmtDate(r.pay_date),
      r.employee_count || 0,
      fmtMoney(r.total_net),
      r.status,
    ]);
    const csv = [header, ...rows]
      .map((row) =>
        row
          .map((v) => (String(v).indexOf(",") >= 0 ? '"' + v + '"' : v))
          .join(",")
      )
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "pay_runs_" + new Date().toISOString().slice(0, 10) + ".csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div
      style={{
        background: C.page,
        minHeight: "100vh",
        fontFamily: FONT,
        color: C.ink,
        padding: "28px 32px 100px",
        boxSizing: "border-box",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          fontSize: 12,
          color: C.ink,
          marginBottom: 14,
        }}
      >
        <span
          style={{ fontWeight: 600, opacity: 0.7, cursor: "pointer" }}
          onClick={() => navigate("/payroll/overview")}
        >
          Payroll
        </span>
        <span style={{ opacity: 0.4 }}>/</span>
        <span style={{ fontWeight: 700 }}>Pay runs</span>
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: 28,
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div>
          <h1
            style={{
              margin: 0,
              fontSize: 34,
              fontWeight: 700,
              color: C.ink,
              letterSpacing: "-0.02em",
            }}
          >
            Pay runs
          </h1>
          <div
            style={{
              fontSize: 14,
              color: C.ink,
              fontWeight: 500,
              marginTop: 4,
            }}
          >
            All payroll runs{company ? " for " + company : ""}.
          </div>
        </div>
        <button
          onClick={() => navigate("/payroll/run")}
          style={{
            padding: "12px 20px",
            background: C.brand,
            border: "none",
            borderRadius: 10,
            color: "#fff",
            fontSize: 14,
            fontWeight: 700,
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            fontFamily: FONT,
            boxShadow: "0 1px 2px rgba(21,160,140,0.3)",
          }}
        >
          <Play size={16} /> Run payroll
        </button>
      </div>

      <div
        style={{
          display: "flex",
          gap: 6,
          marginBottom: 20,
          borderBottom: "1px solid " + C.line,
        }}
      >
        <button onClick={() => setTab("all")} style={tabBtnStyle(tab === "all")}>
          All{" "}
          <span style={{ color: C.ink, fontWeight: 700, marginLeft: 4 }}>
            {counts.all}
          </span>
        </button>
        <button
          onClick={() => setTab("drafts")}
          style={tabBtnStyle(tab === "drafts")}
        >
          Drafts{" "}
          <span style={{ color: C.ink, fontWeight: 700, marginLeft: 4 }}>
            {counts.drafts}
          </span>
        </button>
        <button
          onClick={() => setTab("finalized")}
          style={tabBtnStyle(tab === "finalized")}
        >
          Finalized{" "}
          <span style={{ color: C.ink, fontWeight: 700, marginLeft: 4 }}>
            {counts.finalized}
          </span>
        </button>
      </div>

      <div
        style={{
          paddingBottom: 8,
          borderBottom: "1.5px solid " + C.ink,
          marginBottom: 12,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: C.ink }}>
          {sectionTitle}
        </h2>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <FilterMenu
            sort={sort}
            period={period}
            onSortChange={setSort}
            onPeriodChange={setPeriod}
          />
          <button onClick={handleExportCSV} style={outlineBtnStyle}>
            Export CSV
          </button>
        </div>
      </div>

      {loading && (
        <div
          style={{
            padding: "48px 20px",
            textAlign: "center",
            color: C.ink,
            fontSize: 14,
          }}
        >
          Loading pay runs...
        </div>
      )}

      {errMsg && !loading && (
        <div
          style={{
            padding: 16,
            background: "#FEE2E2",
            border: "1px solid #F87171",
            borderRadius: 10,
            color: "#991B1B",
            fontSize: 13,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <strong>Could not load:</strong> {errMsg}
          </div>
          <button onClick={() => refetch()} style={outlineBtnStyle}>
            Try again
          </button>
        </div>
      )}

      {!loading && !errMsg && filtered.length === 0 && (
        <div style={{ padding: "60px 20px", textAlign: "center" }}>
          <FileText
            size={48}
            style={{ color: C.ink, opacity: 0.4, marginBottom: 12 }}
          />
          <div
            style={{
              fontSize: 18,
              fontWeight: 700,
              color: C.ink,
              marginBottom: 6,
            }}
          >
            {tab === "drafts"
              ? "No drafts"
              : tab === "finalized"
              ? "No finalized runs yet"
              : "No pay runs yet"}
          </div>
          <div
            style={{
              fontSize: 14,
              color: C.ink,
              maxWidth: 400,
              margin: "0 auto 16px",
            }}
          >
            {tab === "drafts"
              ? "All your pay runs have been finalized."
              : tab === "finalized"
              ? "Submit a pay run to see it here."
              : "Create your first pay run to calculate gross pay, taxes, and net pay in one cycle."}
          </div>
          {tab === "all" && (
            <button
              onClick={() => navigate("/payroll/run")}
              style={{
                padding: "12px 20px",
                background: C.brand,
                border: "none",
                borderRadius: 10,
                color: "#fff",
                fontSize: 14,
                fontWeight: 700,
                cursor: "pointer",
                fontFamily: FONT,
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <Play size={16} /> Run your first payroll
            </button>
          )}
        </div>
      )}

      {!loading && !errMsg && filtered.length > 0 && (
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13.5 }}>
          <thead>
            <tr>
              <th style={thStyle}>PAY PERIOD</th>
              <th style={thStyle}>PAY DATE</th>
              <th style={{ ...thStyle, textAlign: "right" }}>EMPLOYEES</th>
              <th style={{ ...thStyle, textAlign: "right" }}>NET TOTAL</th>
              <th style={thStyle}>STATUS</th>
              <th style={{ ...thStyle, textAlign: "right" }}>OPEN</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <PayRunRow key={String(r.id)} payRun={r} />
            ))}
          </tbody>
        </table>
      )}

      <div
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          background: C.card,
          padding: "16px 32px",
          borderTop: "1px solid " + C.line,
          boxShadow: "0 -4px 12px rgba(0,0,0,0.06)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          zIndex: 100,
        }}
      >
        <button
          onClick={() => navigate("/payroll/overview")}
          style={footerBtnStyle}
        >
          &larr; Back to Payroll
        </button>
        <button onClick={handleExportCSV} style={footerBtnStyle}>
          Export CSV
        </button>
      </div>
    </div>
  );
}